import {
  DesignTokenSizeEnum,
  Text,
  TokenTextAppearance,
  TokenTextColor,
} from '@volkswagen-onehub/components-core';
import { useCallback, useEffect, useState } from 'react';
import useGlobalStore from '@/stores/globalStore';
import { Footer, Footnote, PageContainer, PageSection } from '../components';

import {
  performVWBasicErrorMessageLoad,
  performVWBasicMultipleVehiclesPageLoad,
} from '@/analytics/events';
import { SectionNames } from '@/analytics/sectionNames';
import { useTribalContext } from '@/context/tribal';
import useResizeResponsive from '@/hooks/useResizeResponsive';
import { DisclaimerText } from '@/styles';
import CompareHero from '../features/compare/CompareHero/CompareHero';
import VehicleCards from '../features/compare/VehicleCards/VehicleCards';
import AccordionUnderstandingPb from '../features/components/AccordionUnderstandingPb/AccordionUnderstandingPb';
import KeyFeatures from '../layouts/compare/KeyFeatures/KeyFeatures';
import ModelFeatures from '../layouts/compare/ModelFeatures/ModelFeatures';
import ModelSpecifications from '../layouts/compare/ModelSpecifications/ModelSpecifications';
import TechnologyVideos from '../layouts/compare/TechnologyVideos/TechnologyVideos';
import { isMobile } from '../utils/breakpoints';
import { getDisabledModelMessage, isNotBuildable } from '../utils/storeUtils';
import FinanceOptions from '../layouts/compare/FinanceOptions/FinanceOptions';

const Compare = () => {
  const { configurations, retailers, pdbId, fleetMode } = useGlobalStore(
    (state) => state.app
  );
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const setActiveSlideIndex = useGlobalStore(
    (state) => state.setActiveSlideIndex
  );
  const [resetSlides, setResetSlides] = useState(false);
  const isMobileWidth = useResizeResponsive(isMobile);
  const [hasNoFinance, setHasNoFinance] = useState(true);

  const handleSlideChange = useCallback((index: number) => {
    setActiveSlideIndex(index);
    setResetSlides(false);
  }, []);

  const analyticsCommonFields = {
    configurations,
    retailers,
    analyticsManager,
    analyticsBuilder,
  };

  useEffect(() => {
    //When routing from previous page, document is offset
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
    performVWBasicMultipleVehiclesPageLoad(analyticsCommonFields);
  }, []);

  useEffect(() => {
    setActiveSlideIndex(0);
    setResetSlides(true);
  }, [isMobileWidth]);

  useEffect(() => {
    configurations.forEach((configuration) => {
      const { pdbData, modelTitle, trimTitle, derivativeTitle } = configuration;
      if (isNotBuildable(pdbData))
        performVWBasicErrorMessageLoad({
          ...analyticsCommonFields,
          error: {
            message: getDisabledModelMessage(
              contentManager,
              modelTitle,
              derivativeTitle || trimTitle
            ),
          },
        });
    });

    const noFinance = configurations.every(
      (configuration) =>
        configuration.finance == null ||
        undefined ||
        configuration.finance.length == 0
    );
    setHasNoFinance(noFinance);
  }, []);

  return (
    <>
      <CompareHero />
      <VehicleCards
        configurations={configurations}
        onSlideChange={handleSlideChange}
        pdbId={pdbId}
        resetSlides={resetSlides}
      />
      <PageContainer>
        <DisclaimerText
          b960={{
            marginTop: DesignTokenSizeEnum.static400,
          }}
          marginBottom={DesignTokenSizeEnum.dynamic0130}
        >
          <Text
            appearance={TokenTextAppearance.copy100}
            color={TokenTextColor.tertiary}
          >
            {contentManager.get('prices-disclaimer-text')}
          </Text>
        </DisclaimerText>
        <PageSection>
          <AccordionUnderstandingPb
            sectionName={SectionNames.COMPARE_PAGE_HEADER}
            configurations={configurations}
          />
        </PageSection>
        <PageSection>
          <KeyFeatures />
        </PageSection>
        <PageSection>
          <ModelFeatures />
        </PageSection>
        <PageSection>
          <TechnologyVideos />
        </PageSection>
        <PageSection>
          <ModelSpecifications />
        </PageSection>
        {!hasNoFinance && !fleetMode && (
          <PageSection>
            <FinanceOptions />
          </PageSection>
        )}
      </PageContainer>
      <Footer showComparisonView />
      <Footnote />
    </>
  );
};

export default Compare;
